/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogProps } from '@fluentui/react/lib/Dialog';
import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { SearchStrategy } from 'src/core/utils/searchStrategies';
import { LuisId } from 'src/luis/types/Resource';
import { AppsToExportTable } from 'src/verticals/azureMigrationModal/newComponents/AppsToExportTable';
import { MigrationModalFooter } from 'src/verticals/azureMigrationModal/newComponents/MigrationModalFooter';
import { AppToExportStore } from 'src/verticals/azureMigrationModal/stores/AppToExportStore';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';

export type ExportAppsScreenProps = IDialogProps & {
    userMigrationInfo: UserMigrationInfoStore[];
    appsWithOwners: AppToExportStore[];
    onPrevious: () => void;
    onContinue: () => void;
};

const messages = defineMessages({
    title: {
        id: 'multiRegionMigrationModal.exportAppsScreen.title',
        defaultMessage: 'Export apps shared with you'
    },
    subTitle: {
        id: 'multiRegionMigrationModal.exportAppsScreen.subTitle',
        defaultMessage: `Once you have migrated your account to Azure,
you will no longer have access to apps shared with you.
You can make a copy of the currently active version by exporting the data as JSON.`
    },
    exportInstructions: {
        id: 'multiRegionMigrationModal.exportAppsScreen.exportInstructions',
        defaultMessage: 'Select the collaborators you would like to notify.'
    },
    searchPlaceholder: {
        id: 'multiRegionMigrationModal.exportAppsScreen.searchPlaceholder',
        defaultMessage: 'Search by app name ...'
    },
    exportAppToastStart: {
        id: 'multiRegionMigrationModal.exportAppsScreen.exportAppToastStart',
        defaultMessage: 'Exporting app ...'
    },
    exportAppToastSuccess: {
        id: 'multiRegionMigrationModal.exportAppsScreen.exportAppToastSuccess',
        defaultMessage: 'Export app completed'
    },
    exportAppToastFailed: {
        id: 'multiRegionMigrationModal.exportAppsScreen.exportAppToastFailed',
        defaultMessage: 'Export app failed'
    },
    predicationResourceWarning: {
        id: 'multiRegionMigrationModal.exportAppsScreen.predicationResourceWarning',
        defaultMessage: `* These apps use prediction resources in your account. After migration,
you will no longer have access to these apps but they will still be able to use your prediction resource until you rotate the keys.`
    },
    continueButton: {
        id: 'multiRegionMigrationModal.exportAppsScreen.continueButton',
        defaultMessage: 'Continue'
    },
    previousButton: {
        id: 'multiRegionMigrationModal.exportAppsScreen.previousButton',
        defaultMessage: 'Back'
    }
});

export const ExportAppsScreen = withModalMounter((props: ExportAppsScreenProps) => {
    const { appsWithOwners, userMigrationInfo, onContinue, onPrevious } = props;
    const { selection } = useSelection<AppToExportStore>();
    const { dispatcher } = useGlobalState();
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');

    const filteredApps = orderBy(SearchStrategy.substringSearchStrategy(appsWithOwners, query, 'appName'), ['appName'], ['desc']);
    const unOwnedAppsWithPredicationKeysExist = appsWithOwners.some(app => app.hasPredictionResource === true);

    /**
     * Exports the application selected in JSON format by calling the
     * appropriate application handler method, passing it the selected id.
     *
     * @param id the luis id of the app to be exported
     */
    const handleExport = (appId: LuisId, appName: string, region: AuthoringRegion): void => {
        const { serviceKey, endpoint } = userMigrationInfo.find(info => info.region === region);

        dispatcher.dispatch('exportAppFromMigrationModal', {
            appName,
            endpoint,
            serviceKey,
            appId: appId as string,
            toastData: {
                running: { title: localize(messages.exportAppToastStart) },
                completed: { title: localize(messages.exportAppToastSuccess) },
                failed: { title: localize(messages.exportAppToastFailed) }
            }
        });
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 40 }}>
            <Stack verticalFill tokens={{ childrenGap: 40 }}>
                <Stack tokens={{ childrenGap: 15 }}>
                    <LuisText variant="xLarge" fontWeight={600}>
                        {localize(messages.title)}
                    </LuisText>

                    <LuisText>{localize(messages.subTitle)}</LuisText>

                    <LuisText>{localize(messages.exportInstructions)}</LuisText>

                    <LuisDebouncedSearchBox
                        size={36}
                        value={query}
                        ariaLabel={localize(messages.searchPlaceholder)}
                        placeholder={localize(messages.searchPlaceholder)}
                        onChange={(_, newValue?: string) => setQuery(newValue)}
                        debounceTime={0}
                    />

                    <AppsToExportTable
                        data-automation-id="AppsToExportTable"
                        items={filteredApps}
                        selection={selection}
                        onExportApp={handleExport}
                        areItemsFiltered={Boolean(query)}
                        overallItemsCount={filteredApps.length}
                    />
                </Stack>

                {unOwnedAppsWithPredicationKeysExist ? (
                    <LuisText data-automation-id="predictionKeyTextField" variant="small">
                        {localize(messages.predicationResourceWarning)}
                    </LuisText>
                ) : null}
            </Stack>

            <MigrationModalFooter backDisabled={false} nextDisabled={false} onContinue={onContinue} onPrevious={onPrevious} />
        </Stack>
    );
});
